import React, {
  useState,
  useEffect,
  useContext,
  forwardRef,
} from "react";
import "./MonsterUsaCard.css";
import expIcon from "../../../Assets/Images/dice-exp-icon.svg";
import Button from "../../../Common/Button/Button";
import CustomTooltip from "../../../Common/Tooltip/Tooltip";
import {
  applyEllipsis,
  toCapitalize,
  roundUpExperience,
} from "../../../Common/Common-Function/CommonFunctions";
import ActiveCandidateIcon from "../../../Assets/Images/active-candidate-icon.svg";
import { DynamicContext } from "../../../Context/DynamicContext";
import LocationIcon from "../../../Assets/Images/dice-location-icon.svg";
import VisaIcon from "../../../Assets/Images/dice-visa-icon.svg";

const formatWillingToRelocate = (val) => {
  if (val === true || val === "true" || String(val).toLowerCase() === "yes") {
    return "Yes";
  }
  if (val === false || val === "false" || String(val).toLowerCase() === "no") {
    return "No";
  }
  return null;
};

const MonsterUsaCard = forwardRef((
  {
    name,
    role,
    experience,
    location,
    workAuthorizations = [],
    degree,
    skills = [],
    cardColor = "var(--header__color)",
    cardbackgroundColor = "var(--card__background)",
    skillColor = "var(--skill-badge-color)",
    skillbackgroundColor = "var(--skill-badge-bg)",
    cardWidth,
    viewEvent = () => "",
    showView = true,
    viewDisabled = false,
    cardBorder,
    cardView = "compact",
    isDetailed = false,
    willRelocate,

  },
  ref
) => {
  const { isDarkMode } = useContext(DynamicContext);

  const isDetailedCard = isDetailed || cardView === "detailed";

  const workPermits = (
    Array.isArray(workAuthorizations) ? workAuthorizations : [workAuthorizations]
  )
    .map((permit) => {
      if (typeof permit === "string") return permit.trim();
      if (typeof permit?.authorization !== "string" || !permit.authorization.trim()) return "";
      const authorization = permit.authorization.trim();
      return permit.country ? `${authorization} (${permit.country})` : authorization;
    })
    .filter(Boolean);

  const formattedRelocate = formatWillingToRelocate(willRelocate);
  const formattedDegree = typeof degree === "string" && degree.trim()
    ? degree.trim()
    : "Not specified";

  const skillsArray =
    typeof skills === "string"
      ? skills
          .split(",")
          .map((skill) => skill.trim())
          .filter((skill) => skill && skill.trim() !== "")
      : Array.isArray(skills)
        ? skills.filter((skill) => typeof skill === "string" && skill.trim())
        : [];

  const [windowWidth, setWindowWidth] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth : 1200
  );

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const ellipsisLength = isDetailedCard
    ? 60
    : windowWidth > 1800
      ? 50
      : windowWidth <= 768 && windowWidth >= 425
        ? 30
        : 20;
  const visibleSkillsCount = 2;

  const displayedSkills = skillsArray.slice(0, visibleSkillsCount);
  const skillsToDisplay = isDetailedCard ? skillsArray : displayedSkills;

  return (
    <div
      className={`monster-usa-card ${isDetailedCard ? "detailed-card" : ""}`}
      style={{
        color: cardColor,
        background: cardbackgroundColor,
        width: cardWidth,
        border: cardBorder,
        position: "relative",
      }}
      ref={ref}
    >
      <div className="card-header-container">
        <div className="candidate-card-header">
          <div className="card-header-user">
            <div className="header-and-tags">
              <div className="card-header-text-container">
                <div className="candidate-icon">
                  <img
                    src={ActiveCandidateIcon}
                    alt="Candidate Icon"
                    className="card-candidate-icon"
                  />
                </div>
                <div className="card-header-text">
                  <h3>
                    {name && typeof name === "string" && name.trim()
                      ? applyEllipsis(toCapitalize(name), 30)
                      : "N/A"}
                  </h3>
                  {role && (
                    <div
                      className="card-details-span-candidate"
                      style={{ minWidth: 0, overflow: "hidden" }}
                    >
                      <CustomTooltip title={role} arrow>
                        <p className="card-subheader-candidate">
                          {applyEllipsis(role, ellipsisLength)}
                        </p>
                      </CustomTooltip>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="contact-info">
            <div className="card-exp-details-span">
              <img
                src={expIcon}
                alt="Experience"
                className="card-icon"
                style={{ width: "20px" }}
              />
              <p className="card-subheader-candidate">
                {roundUpExperience(experience)}
              </p>
            </div>
            {!isDetailedCard && location && (
              <div
                className="card-details-span-candidate"
                style={{ minWidth: 0, overflow: "hidden" }}
              >
                <img
                  src={LocationIcon}
                  alt="Location"
                  className="card-icon"
                  style={{ width: "20px" }}
                />
                <CustomTooltip title={location} arrow>
                  <p className="card-subheader-candidate">
                    {applyEllipsis(location, ellipsisLength)}
                  </p>
                </CustomTooltip>
              </div>
            )}
          </div>
        </div>
      </div>

      {isDetailedCard && (
        <div className="monster-usa-detailed-info-card">
          <div className="monster-usa-detail-item monster-usa-detail-item-location">
            <span className="monster-usa-detail-label"><img src={LocationIcon} alt="LocationIcon"/>LOCATION</span>
            <div className="monster-usa-detail-value">
              {location && location !== "N/A" ? (
                <CustomTooltip title={location} arrow>
                  <span>{applyEllipsis(location, 50)}</span>
                </CustomTooltip>
              ) : (
                <span className="monster-usa-detail-empty">Not specified</span>
              )}
            </div>
          </div>

          <div className="monster-usa-detail-item">
            <span className="monster-usa-detail-label"><img src={VisaIcon} alt="VisaIcon"/>WORK AUTHORIZATION</span>
            <div className="monster-usa-detail-value">
              {workPermits.length > 0 ? (
                workPermits.map((permit, index) => {
                  const isGreenCard = /green card/i.test(permit);
                  const isCitizen = /citizen/i.test(permit);
                  const badgeClass = isGreenCard
                    ? "monster-usa-visa-green"
                    : isCitizen
                    ? "monster-usa-visa-blue"
                    : "monster-usa-visa-default";
                  return (
                    <span
                      className={`monster-usa-visa-pill ${badgeClass}`}
                      key={`${permit}-${index}`}
                    >
                      <svg
                        className="monster-usa-visa-shield-icon"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                      </svg>
                      {permit}
                    </span>
                  );
                })
              ) : (
                <span className="monster-usa-detail-empty">Not specified</span>
              )}
            </div>
          </div>

          <div className="monster-usa-detail-item">
            <span className="monster-usa-detail-label">WILLING TO RELOCATE</span>
            <div className="monster-usa-detail-value">
              {formattedRelocate ? (
                <span className="monster-usa-relocate-wrap">
                  <span className="monster-usa-relocate-bullet">•</span>
                  <span>{formattedRelocate}</span>
                </span>
              ) : (
                <span className="monster-usa-detail-empty">Not specified</span>
              )}
            </div>
          </div>

          <div className="monster-usa-detail-item">
            <span className="monster-usa-detail-label">DEGREE</span>
            <div className="monster-usa-detail-value">
              <span className={formattedDegree === "Not specified" ? "monster-usa-detail-empty" : undefined}>
                {formattedDegree}
              </span>
            </div>
          </div>
        </div>
      )}

      <div className="card-contact-info">
        <div className="skills-and-view-candidate-card">
          <div className="card-skills">
            <p>Skills</p>
            {skillsToDisplay.length > 0 ? (
              <div className="skills-container">
                {skillsToDisplay.map((skill, index) => (
                  <div
                    className="card-skills-div"
                    key={index}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      width: "auto",
                      maxWidth: "100%",
                      overflow: "visible",
                      flexShrink: 0,
                    }}
                  >
                    <span
                      className="skill-badge"
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: "auto",
                        maxWidth: "none",
                        minWidth: "fit-content",
                        overflow: "visible",
                        textOverflow: "clip",
                        whiteSpace: "nowrap",
                        boxSizing: "border-box",
                      }}
                    >
                      {isDetailedCard
                        ? typeof skill === "string"
                          ? skill.trim()
                          : skill
                        : applyEllipsis(skill, 5)}
                    </span>
                  </div>
                ))}
                {!isDetailedCard && skillsArray.length > visibleSkillsCount && (
                  <CustomTooltip
                    className="skill-badge"
                    pill={false}
                    title={
                      <div
                        style={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: "5px",
                          maxWidth: "300px",
                          maxHeight: "100px",
                          overflowY: "auto",
                        }}
                      >
                        {skillsArray.map((skill, index) => (
                          <span
                            key={index}
                            className="skill-tooltip-badge"
                            style={{
                              color: skillColor,
                              background: skillbackgroundColor,
                            }}
                          >
                            {skill}
                          </span>
                        ))}
                      </div>
                    }
                    placement="top"
                    arrow
                  >
                    <div className="card-skills-div">
                      <span
                        className="skill-badge"
                        style={{
                          cursor: "pointer",
                        }}
                      >
                        +{skillsArray.length - visibleSkillsCount}
                      </span>
                    </div>
                  </CustomTooltip>
                )}
              </div>
            ) : (
              <p className="no-skills">—</p>
            )}
          </div>
          <div className="view-container">
            {showView && (
              <Button
                label="View"
                event={viewEvent}
                disabled={viewDisabled}
                className="view"
                backgroundColor="none"
                color={isDarkMode ? "#aea9ff" : "#6860EE"}
                border={isDarkMode ? "1px solid #aea9ff" : "1px solid #6860EE"}
                style={{
                  padding: "4px 12px",
                  cursor: viewDisabled ? "not-allowed" : "pointer",
                  opacity: viewDisabled ? 0.5 : 1,
                  backgroundColor: "transparent",
                  color: isDarkMode ? "#aea9ff" : "#6860EE",
                  border: isDarkMode ? "1px solid #aea9ff" : "1px solid #6860EE",
                  borderRadius: "8px",
                }}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
});

MonsterUsaCard.displayName = "MonsterUsaCard";

export default MonsterUsaCard;
